import React, { useContext, useState, useEffect, useCallback } from 'react';
import { Row, Col } from 'react-bootstrap';
import { AccountContext } from '../../Accounts';
import { useTranslation } from 'react-i18next';
import Spinner from 'react-bootstrap/Spinner';
import { API } from '../../Constants';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Link } from "react-router-dom";
import AppointmentCard from './AppointmentCard';
import { useCustomTranslator } from '../../utils/customTranslator';
import { DataTypeContext } from '../../DataTypes';

const UpcomingAppointments = () => {
    const { clientId } = useContext(AccountContext);
    const { dataTypes } = useContext(DataTypeContext);
    const { t } = useTranslation();
    const customT = useCustomTranslator();
    const { executeRecaptcha } = useGoogleReCaptcha();
    const [upcomingAppointments, setUpcomingAppointments] = useState(null);
    const [appointmentsLoading, setAppointmentsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState('');
    const [errorClass, setErrorClass] = useState('text-danger');

    const fetchAppointments = useCallback(async () => {
        setAppointmentsLoading(true);
        setErrorMessage('');
        setErrorClass('text-danger');
        const token = await executeRecaptcha("details");
        const res = await fetch(`${API.UPCOMING_APPOINTMENTS_URL}/${clientId}?token=${token}`);
        await res.json()
            .then(data => {
                setUpcomingAppointments(res.ok ? data : []);
                setAppointmentsLoading(false);
                if (!res.ok && data.code) {
                    const customError = customT(`api.error.${data.code}`) || data?.message || data?.error;
                    if (data.code === 'APPOINTMENT_UPCOMING_NOT_FOUND') {
                        setErrorClass('text-warning');
                    }
                    setErrorMessage(customError);
                } else if (!res.ok && data.error) {
                    setErrorMessage(data.error);
                }
            })
            .catch(err => {
                const customError = customT('api.error.UNKNOWN');
                setErrorMessage(customError);
                setUpcomingAppointments([]);
                setAppointmentsLoading(false);
                console.error('error fetching appointments', err);
            });
    }, [clientId, executeRecaptcha]);

    const handleCancel = async (appointment) => {
        const confirmed = window.confirm(t('upcomingAppointments.cancelConfirmation'));
        if (confirmed) {
            setErrorMessage('');
            setErrorClass('text-danger');
            const token = await executeRecaptcha("details");
            const deleteURL = `${API.DELETE_APPOINTMENTS_URL}/${appointment.location.id}/${appointment.id}`;
            fetch(deleteURL, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({token: token}),
            })
                .then((response) =>
                    response.json().then(data => {
                        if (response.ok) {
                            fetchAppointments();
                        } else if (!response.ok && data.code) {
                            const customError = customT(`api.error.${data.code}`) || data?.message || data?.error;
                            setErrorMessage(customError);
                        } else {
                            const customError = customT('api.error.UNKNOWN');
                            setErrorMessage(customError);
                        }
                    })
                )
                .catch((e) => {
                    const customError = customT('api.error.UNKNOWN');
                    setErrorMessage(customError);
                    console.error('error cancelling appointment', e);
                })
        }
    };

    useEffect(() => {
        fetchAppointments();
    }, [fetchAppointments]);

    const appointmentCutOffHours = dataTypes?.appointmentOptions?.appointmentCutOffHours || 48;
    const supportNotice = t('upcomingAppointments.supportNotice', { hours: appointmentCutOffHours });

    return (
        <Col md={6} sm={12} className="mx-auto">
            <section className="card shadow-sm rounded p-3 p-md-4">
                <h4 className="text-center mb-4">{t('upcomingAppointments.heading')}</h4>

                {appointmentsLoading && (
                    <div className="text-center py-4">
                        <Spinner animation="border" variant="primary" />
                    </div>
                )}

                {errorMessage && (
                    <p className={`text-center ${errorClass}`}>{errorMessage}</p>
                )}

                {!appointmentsLoading && upcomingAppointments.length > 0 && (
                    <>
                        {upcomingAppointments.map((appointment, i) => (
                            <AppointmentCard
                                key={`appointment-${i}`}
                                appointment={appointment}
                                variant="upcoming"
                                showActions={appointment.canModify}
                                onCancel={() => handleCancel(appointment)}
                                errorMessage={
                                    appointment.canModify
                                        ? ''
                                        : customT('api.error.APPOINTMENT_NO_MODIFY_OR_CANCEL', {
                                            hours: appointmentCutOffHours,
                                        })
                                }
                            />
                        ))}

                        <div className="card bg-light border mb-2">
                            <div className="card-body py-3 text-secondary">
                                {supportNotice}
                            </div>
                        </div>
                    </>
                )}
            </section>

            <p className="text-center mt-4 mb-0">
                <Link to="/dashboard">{t('dashboard.return')}</Link>
            </p>
        </Col>
    );
};

export default UpcomingAppointments;